import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';

  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Icon to display before text
   */
  iconBefore?: ReactNode;

  /**
   * Icon to display after text
   */
  iconAfter?: ReactNode;

  /**
   * Full width button
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Button content
   */
  children?: ReactNode;
}

/**
 * Button component
 *
 * A versatile button component with multiple variants, sizes, and states.
 * Supports loading state, icons, and full accessibility.
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">
 *   Click me
 * </Button>
 *
 * <Button variant="outline" loading>
 *   Loading...
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      iconBefore,
      iconAfter,
      fullWidth = false,
      disabled,
      className,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const baseStyles = [
      'inline-flex items-center justify-center',
      'font-medium rounded-lg',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ];

    const variantStyles = {
      primary: [
        'bg-primary-600 text-white',
        'hover:bg-primary-700',
        'focus:ring-primary-500',
        'active:bg-primary-800',
        'dark:bg-primary-500 dark:hover:bg-primary-600 dark:active:bg-primary-700',
      ],
      secondary: [
        'bg-secondary-600 text-white',
        'hover:bg-secondary-700',
        'focus:ring-secondary-500',
        'active:bg-secondary-800',
        'dark:bg-secondary-500 dark:hover:bg-secondary-600 dark:active:bg-secondary-700',
      ],
      outline: [
        'border-2 border-primary-600 text-primary-600',
        'hover:bg-primary-50',
        'focus:ring-primary-500',
        'active:bg-primary-100',
        'dark:border-primary-400 dark:text-primary-400',
        'dark:hover:bg-primary-950 dark:active:bg-primary-900',
      ],
      ghost: [
        'text-gray-700',
        'hover:bg-gray-100',
        'focus:ring-gray-500',
        'active:bg-gray-200',
        'dark:text-gray-300',
        'dark:hover:bg-gray-700 dark:active:bg-gray-600',
      ],
      danger: [
        'bg-error-600 text-white',
        'hover:bg-error-700',
        'focus:ring-error-500',
        'active:bg-error-800',
        'dark:bg-error-500 dark:hover:bg-error-600 dark:active:bg-error-700',
      ],
    };

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    const widthStyles = fullWidth ? 'w-full' : '';

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(baseStyles, variantStyles[variant], sizeStyles[size], widthStyles, className)}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {!loading && iconBefore && <span className="mr-2 flex items-center">{iconBefore}</span>}

        {children}

        {!loading && iconAfter && <span className="ml-2 flex items-center">{iconAfter}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
